import {
  Box,
  Button,
  marketingBannerClassNames,
  MarketingBanner,
} from '@cast/design-system';

export type ExploreAutomationBannerVariant =
  | 'dashboard'
  | 'cluster-score'
  | 'available-savings';

type ExploreAutomationBannerProps = {
  variant: ExploreAutomationBannerVariant;
  onExplore: () => void;
  /** Dashboard only — when true, shows close control. */
  closable?: boolean;
  onClose?: () => void;
};

const CONTINUOUS_OPTIMIZATION_MESSAGE =
  'Let Cast AI continuously optimize this cluster as demand changes — rightsizing workloads, reducing idle capacity, and choosing more efficient nodes.';

const COPY: Record<
  ExploreAutomationBannerVariant,
  { title: string; message: string }
> = {
  dashboard: {
    title: 'Go beyond reporting',
    message: CONTINUOUS_OPTIMIZATION_MESSAGE,
  },
  'cluster-score': {
    title: 'Improve what’s behind your score',
    message:
      'Cast AI can continuously rightsize workloads, reduce idle capacity, and optimize infrastructure as demand changes.',
  },
  'available-savings': {
    title: 'Turn savings insights into continuous optimization',
    message: CONTINUOUS_OPTIMIZATION_MESSAGE,
  },
};

const EXPLORE_AUTOMATION_END_CONTENT_SRC = new URL(
  './assets/explore-automation-end-content.svg',
  import.meta.url
).href;

/** Figma Monetization 325:170767 end-content slot (exported SVG). */
const DashboardEndContent = () => (
  <Box
    component="img"
    src={EXPLORE_AUTOMATION_END_CONTENT_SRC}
    alt=""
    width={344}
    height={116}
    data-testid="explore-automation-end-content"
    sx={{
      display: 'block',
      width: 344,
      height: 116,
      maxWidth: '100%',
      flexShrink: 0,
    }}
  />
);

export const ExploreAutomationBanner = ({
  variant,
  onExplore,
  closable = false,
  onClose,
}: ExploreAutomationBannerProps) => {
  const { title, message } = COPY[variant];
  const isDashboard = variant === 'dashboard';

  return (
    <MarketingBanner
      key={`explore-automation-${variant}`}
      testId={`explore-automation-banner-${variant}`}
      title={title}
      message={message}
      closable={closable}
      onClose={onClose}
      endContent={isDashboard ? <DashboardEndContent /> : undefined}
      sx={
        isDashboard
          ? {
              // Figma end slot is 344px; DS default is MetricsBox-sized (98px).
              [`& .${marketingBannerClassNames.endContent}`]: {
                width: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                overflow: 'visible',
              },
            }
          : undefined
      }
      action={
        <Button
          variant="primary"
          size="sm"
          onClick={onExplore}
          testId="explore-automation-cta"
        >
          Explore automation
        </Button>
      }
    />
  );
};
